import { useState } from 'react';
import { Alert } from './Alert.js';

/**
 * Modal for actions that require a `reason` string — approve + void.
 * Enforces min 1 / max 500 chars client-side (mirrors server Zod schema).
 * Server is the source of truth; an empty reason would 400 anyway.
 *
 * Headless: no backdrop click-to-close, no focus trap. Sprint 0 keeps it
 * simple. Upgrade to Radix Dialog when we have more modals than just these
 * two.
 */
export function ReasonModal(props: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  confirmVariant: 'primary' | 'danger';
  isSubmitting: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}): JSX.Element | null {
  const {
    open,
    title,
    description,
    confirmLabel,
    confirmVariant,
    isSubmitting,
    errorMessage,
    onCancel,
    onConfirm,
  } = props;
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const trimmed = reason.trim();
  const tooShort = trimmed.length < 1;
  const tooLong = trimmed.length > 500;
  const disabled = tooShort || tooLong || isSubmitting;

  if (!open) return null;

  const confirmClasses =
    confirmVariant === 'danger'
      ? 'bg-red-600 text-white hover:bg-red-500'
      : 'bg-white text-black hover:bg-gray-200';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reason-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
    >
      <div className="w-full max-w-md rounded border border-gray-800 bg-gray-950 p-6 space-y-4">
        <header className="space-y-1">
          <h2 id="reason-modal-title" className="font-serif text-xl text-white">
            {title}
          </h2>
          <p className="text-sm text-gray-400">{description}</p>
        </header>

        <div className="space-y-1">
          <label
            htmlFor="reason-modal-reason"
            className="block text-xs uppercase tracking-wide text-gray-400"
          >
            Reason *
          </label>
          <textarea
            id="reason-modal-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            aria-invalid={touched && (tooShort || tooLong) ? 'true' : 'false'}
            className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
            placeholder="Written record for the audit trail. E.g. &quot;April payroll, verified against September budget.&quot;"
          />
          {touched && tooShort ? (
            <p className="text-xs text-red-400">Reason is required.</p>
          ) : null}
          {tooLong ? (
            <p className="text-xs text-red-400">Reason must be 500 characters or fewer.</p>
          ) : null}
        </div>

        {errorMessage ? <Alert variant="error">{errorMessage}</Alert> : null}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded border border-gray-700 px-4 py-2 text-sm hover:bg-gray-900 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setTouched(true);
              if (!tooShort && !tooLong) onConfirm(trimmed);
            }}
            disabled={disabled}
            className={`rounded px-4 py-2 text-sm font-medium disabled:opacity-50 ${confirmClasses}`}
          >
            {isSubmitting ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

